import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, Link, useLocation, useParams } from "react-router-dom";
import { FaCalendarAlt } from "react-icons/fa";
import "./review.css";
import "./UpdateGoal.css";
import { FaArrowLeft } from "react-icons/fa";
import Select from "react-select";
import Toast from "./Toast";
//images
import PEE from "../assets/PictureEE.jpg";
import PHP from "../assets/PictureHP.jpg";
import PSC from "../assets/PictureSC.jpg";
import PPC from "../assets/PicturePC.jpg";
import PP from "../assets/PictureP.jpg";
import PEQ from "../assets/Harry_Stills38.jpg";
import { API_BASE } from "../apiConfig";
import { withNewestGoalVersion } from "./newestGoalVersion";

const GET_INTERVENTIONS_API =
    `${API_BASE}/therapist_api/get_test_intervention.php`;
const POST_GOALS_API =
    `${API_BASE}/therapist_api/insert_pathway_goals.php`;
const GET_USED_INTERVENTIONS_API =
    `${API_BASE}/therapist_api/get_used_interventions.php`;

// Static data used to render the 5 "Foundational Interventions" info cards.
const FOUNDATIONAL_CARDS = [
    {
        title: "Environmental Enrichment",
        image: PEE,
        description:
            "Environmental enrichment is the act of adapting or enhancing an infant's environment to support their learning and development. Environmental enrichment may target cognitive, motor, sensory and social development, and always aims to support neuroplasticity, skill acquisition and goal attainment through everyday activities and routines.",

        keyComponents: [
            {
                title: "Cognitive enrichment",
                text: "Provide opportunities for problem solving, learning, attention and exploration."
            },
            {
                title: "Motor enrichment",
                text: "Adapt toys, objects and environments to encourage active movement, repetition, practice and skill development."
            },
            {
                title: "Social enrichment",
                text: "Provide opportunities for positive social interactions, empathic responses to the infant's expressed needs, and emotional connections with caregivers, family members and peers."
            },
            {
                title: "Sensory enrichment",
                text: "Enhance or adapt environments to support visual, hearing and tactile needs while avoiding sensory overload."
            },
            {
                title: "Optimising learning state",
                text: "Address factors that influence readiness to learn, such as sleep, pain, fatigue, nutrition and emotional wellbeing."
            }
        ],

        footer: "For more information see CPAdvance Environmental Enrichment."
    },

    {
        title: "Home Programs",
        image: PHP,
        description:
            "Home programs are therapeutic activities that are completed by an infant and their caregiver/s within the home (or other) environment to achieve specific goals. Effective home programs are family-centred, based on caregiver-led goals, and help caregivers to build confidence and skills to support their infant's development in everyday life. Home programs are essential for achieving dosage of therapy.",

        keyComponents: [
            {
                title: "Collaborative partnerships",
                text: "Provide caregivers with specific guidance to target caregiver-led goals."
            },
            {
                title: "Context-specific programs",
                text: "Use the infant's own toys and everyday objects while adapting tasks to suit family routines."
            },
            {
                title: "Written and pictorial materials",
                text: "Use photos and videos from the family's environment as reminders for practice."
            },
            {
                title: "Regular support and feedback",
                text: "Help implementation and maintain caregiver motivation."
            },
            {
                title: "Ongoing evaluation",
                text: "Review progress and goal attainment regularly."
            }
        ],

        footer: "For more information see CPAdvance Home Programs."
    },

    {
        title: "Supporting Caregivers",
        image: PSC,
        description:
            "Supporting caregivers is a family-centred approach that recognises caregivers play the most important role in their infant's development. Therapists support caregivers by building strong relationships, responding to their emotional and practical needs, promoting well-being, and helping them access appropriate supports.",

        keyComponents: [
            {
                title: "Be Present",
                text: "Listen actively, respond with empathy and create a safe, non-judgemental space."
            },
            {
                title: "Empower",
                text: "Use education, coaching and strengths-based approaches to build caregiver confidence and capability."
            },
            {
                title: "Detect",
                text: "Recognise signs of caregiver stress or distress and respond appropriately."
            },
            {
                title: "Connect",
                text: "Link caregivers with supports, resources, peer networks and services when needed."
            },
            {
                title: "Family-centred practice",
                text: "Recognise each family as unique and work collaboratively to support whole-family well-being."
            }
        ],

        footer: "For more information see CPAdvance Supporting Caregivers."
    },

    {
        title: "Parent Coaching",
        image: PPC,
        description:
            "Parent coaching is a family-centred approach that empowers caregivers as the experts in their child's journey, building on their strengths and equipping them with skills to navigate challenges and achieve meaningful goals. Through a collaborative partnership, therapists and caregivers share knowledge, problem-solve together, and identify strategies that can be embedded into everyday routines and activities.",

        keyComponents: [
            {
                title: "Joint planning",
                text: "Real-world problem solving to integrate therapy activities into everyday life."
            },
            {
                title: "Observation",
                text: "Observe caregivers carrying out activities in natural environments."
            },
            {
                title: "Action and practice",
                text: "Support infants and caregivers to practise goals within and between therapy sessions."
            },
            {
                title: "Reflection and problem-solving",
                text: "Overcome challenges, build caregiver capacity and guide therapist support."
            },
            {
                title: "Feedback",
                text: "Provide informative, affirmative, evaluative and directive feedback tailored to caregiver needs."
            }
        ],

        footer: "For more information see CPAdvance Coaching."
    },

    {
        title: "Play",
        image: PP,
        description:
            "Play is an essential part of childhood that supports learning, development and participation. Infants learn best when they are engaged, motivated and having fun. In therapy, play should be child-led and integrated into intervention activities to create meaningful opportunities for practice, problem-solving and skill development. Understanding how to use play with purpose is fundamental to effective interventions.",

        keyComponents: [
            {
                title: "Child-led experimentation",
                text: "Encourage active problem solving."
            },
            {
                title: "Child preferences",
                text: "Follow the infant's interests and use activities and toys they enjoy."
            },
            {
                title: "Contingency based learning",
                text: "Associating certain behaviours with outcomes."
            },
            {
                title: "Affordances",
                text: "Identify goal limiting factors to allow for adaptation."
            },
            {
                title: "Play across contexts/environments",
                text: "Support participation across contexts."
            },
            {
                title: "Responsive relationships",
                text: "Support responsive interactions between infant and caregivers."
            }
        ],

        footer: "For more information see CPAdvance Play."
    },

    {
        title: "Equipment",
        image: PEQ,
        description:
            "The appropriate use of equipment for infants with cerebral palsy: The first three years are a period of rapid motor development, where harnessing neuroplasticity and natural recovery is paramount. Selective motor control and weakness are responsive to rehabilitation when targeted with active motor learning interventions (Morganet al., 2021). During this critical period, clinicians should carefully consider the role of equipment, balancing opportunities for active motor development. Thoughtful clinical reasoning is required to ensure equipment enhances, rather than limits, learningopportunities.",

        keyComponents: [
            {
                title: "Active motor learning",
                text: "Prioritise interventions that target selective motor control and weakness through active practice."
            },
            {
                title: "Thoughtful clinical reasoning",
                text: "Carefully consider whether equipment supports or limits the infant's opportunities for active movement."
            },
            {
                title: "Balanced opportunities",
                text: "Balance equipment use with active motor development during the critical first three years."
            }
        ],

        footer: "For more information see CPAdvance Equipment."
    }
];

// Default template for a new goal
const createNewGoal = () => ({
    id: Date.now() + Math.random(),
    goalDomainKeys: [],
    goalDomains: [],
    goalSkills: [],
    status: "Select",
    resultDate: "",
    // Without this key the Goal Target Date input renders uncontrolled and
    // buildPayload() posts target_date: undefined, which blanks the date that
    // is already stored for the goal.
    targetDate: "",
    smartGoal: "",
    goalReviewDate: "",
    interventionTypes: [],
    interventions: [{ type: "", minutesPerDay: 0, weeks: 0, therapistSessions: 0 }],
    outcomeMeasure: "",
});

// Coerces a persisted dosage counter back into a number for <input type="number">.
// PDO hands these back as strings, and an absent value must land on 0 rather than
// NaN, which React would render as an invalid field the therapist cannot clear.
const toCount = (value) => {
    if (value === "" || value === null || value === undefined) return 0;
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

// Normalises a "Domain - Skill" label for comparison. The same logical domain
// turns up with different punctuation and spacing depending on whether it came
// from the catalogue or was echoed back from a saved goal, so both are reduced
// to a bare alphanumeric key before being matched.
const normalizeLabel = (name) =>
    String(name || "")
        .toLowerCase()
        .replace(/&/g, "and")
        .replace(/[^a-z0-9]/g, "");

// Tooltip text for the "Previously used" badge. A bare badge is not actionable
// on its own, so it explains where the family was used before - the therapist
// can then decide whether reusing it is intentional.
const describePriorUse = (entry) => {
    if (!entry) return "";
    const count = entry.used_count || 0;
    const times = `${count} time${count === 1 ? "" : "s"}`;

    const last = entry.last_used_at ? String(entry.last_used_at).slice(0, 10) : null;
    const first = entry.first_used_at ? String(entry.first_used_at).slice(0, 10) : null;

    let when = "";
    if (last && first && first !== last) {
        when = ` First used ${first}, most recently ${last}.`;
    } else if (last) {
        when = ` Last used ${last}.`;
    }

    return `This intervention family was already selected for this client ${times}.${when} Selecting it again is allowed.`;
};

// Custom styles for the react-select multi-select dropdown so the opened
// menu reads as a framed "window" expanding from the control/arrow.
const interventionSelectStyles = {
    control: (base, state) => ({
        ...base,
        borderRadius: 8,
        borderColor: state.isFocused ? "#4caf50" : "#d5dbe0",
        boxShadow: state.isFocused ? "0 0 0 1px #4caf50" : base.boxShadow,
    }),
    menu: (base) => ({
        ...base,
        border: "2px solid #4caf50",
        borderRadius: 10,
        boxShadow: "0 10px 28px rgba(0,0,0,0.18)",
        overflow: "hidden",
        transformOrigin: "top center",
        animation: "ug-select-expand 0.18s ease-out",
    }),
    menuList: (base) => ({
        ...base,
        padding: 6,
    }),
};

// Custom styles for the Goal Domain multi-select so the selected domains
// render as chips inside a box matching the goalreview / goalsetdownload
// Goal Domain display (gray chips, #323C43 text, same height, full width).
const goalDomainSelectStyles = {
    ...interventionSelectStyles,
    /* Keep the control exactly the width of its grid column (1fr) and never
       wider, so the box height/width stay as they always were. */
    container: (base) => ({
        ...base,
        width: "100%",
        minWidth: "100%",
        maxWidth: "100%",
    }),
    control: (base, state) => ({
        ...interventionSelectStyles.control(base, state),
        background: "#f9f9f9",
        borderColor: state.isFocused ? "#4caf50" : "#b0bec5",
        borderRadius: 8,
        width: "100%",
    }),
    /* Long domains scroll sideways INSIDE the box instead of being clipped.
       nowrap keeps it a single line, so the control height never changes. */
    valueContainer: (base) => ({
        ...base,
        padding: "4px 8px",
        flexWrap: "nowrap",
        overflowX: "auto",
        overflowY: "hidden",
        alignItems: "center",
        /* room for the horizontal scrollbar without changing the control height */
        paddingBottom: 8,
        scrollbarWidth: "thin",
        "-ms-overflow-style": "thin",
        "::-webkit-scrollbar": { height: 6 },
        "::-webkit-scrollbar-thumb": {
            background: "#b0bec5",
            borderRadius: 3,
        },
    }),
    multiValue: (base) => ({
        ...base,
        background: "#e0e0e0",
        borderRadius: 4,
        /* chips keep their natural width so the text is never squeezed */
        flex: "0 0 auto",
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: "#323C43",
        fontSize: 13,
        fontWeight: 600,
        padding: "2px 10px",
        /* no ellipsis: the whole word must stay readable while scrolling */
        whiteSpace: "nowrap",
        overflow: "visible",
        textOverflow: "clip",
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: "#666",
        borderRadius: "0 4px 4px 0",
        ":hover": { background: "#cfcfcf", color: "#323C43" },
    }),
};

const UpdateGoal = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    // Resolve the session id from whichever routing pattern is being used
    const urlParams = new URLSearchParams(location.search);
    const sessionId =
        params.sessionId ||
        location.state?.sessionId ||
        urlParams.get("sessionId") ||       // camelCase, matches your URL
        urlParams.get("session_id") ||      // keep snake_case as fallback too
        "";

    // Smart Goal mode: when "Revise Selected Intervention" navigates here,
    // the goalSessionId param tells us which session holds the goal being
    // revised. We pass it through to the save endpoint so the revision
    // lands on the correct goal row.
    const isSmartGoal = urlParams.get("smartGoal") === "1";
    const goalSessionId = urlParams.get("goalSessionId") || urlParams.get("goalSession");

    // Review Smart Goal needs TWO sessions, and conflating them broke the
    // revise flow in both directions:
    //
    //   * sessionId  - the Review Smart Goal session. Its answers produced the
    //     Suggested Interventions the therapist just chose from, so the
    //     catalogue (and therefore the Goal Domain chips and the intervention
    //     dropdown) has to come from HERE, not from the originating pathway -
    //     the two pathways have differently-named groups.
    //   * goalSessionId - the session that actually stores the goal row being
    //     revised. This is where the existing SMART goal / dosage / outcome
    //     values are read from and written back to.
    //
    // Interventions.js already passes sessionId = goalSessionId, so without this
    // the page showed the originating pathway's groups; and reading the goals
    // from the RSG session instead would show a blank form, because an RSG
    // session has no goal rows of its own (get_test_intervention.php returns
    // goals: [] for it - the domains are only inherited for the catalogue).
    const goalDataSessionId = (isSmartGoal && goalSessionId) ? goalSessionId : sessionId;
    const needsSeparateGoalFetch = isSmartGoal && goalSessionId && goalSessionId !== sessionId;

    // STATE: Array of goal objects instead of individual variables
    const [goals, setGoals] = useState([createNewGoal()]);

    // Data returned from get_test_intervention.php
    const [sessionData, setSessionData] = useState(null);
    // Review Smart Goal only: the goal session's own payload, merged into
    // sessionData for the pre-fill. Null in every other flow.
    const [goalRowsSessionData, setGoalRowsSessionData] = useState(null);
    const [loadingInterventions, setLoadingInterventions] = useState(true);
    const [loadError, setLoadError] = useState(null);

    // Intervention Families this client has already been prescribed, keyed
    // lowercase. Used only to render the "Previously used" badge - the
    // duplicate check is informational, never blocking.
    const [usedSheets, setUsedSheets] = useState({});
    // Guards the prefill so it runs once per session, after the catalogue and
    // the saved goals have both arrived.
    const prefilledForSessionRef = useRef(null);

    // Which foundational card popup is open (index into FOUNDATIONAL_CARDS, or null)
    const [activeCardIndex, setActiveCardIndex] = useState(null);

    // Submit state for the "Update" button
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [toast, setToast] = useState(null);
    const today = new Date().toISOString().split("T")[0];
    // ---- 1. GET session/intervention data ----
    // Primary fetch: the review session, which is what supplies the Suggested
    // Interventions catalogue. The goal rows are merged in below.
    useEffect(() => {
        const fetchInterventions = async () => {
            try {
                setLoadingInterventions(true);
                setLoadError(null);
                const token = localStorage.getItem("token");
                const res = await fetch(GET_INTERVENTIONS_API, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        session_id: parseInt(sessionId),
                    }),
                });
                const json = await res.json();
                if (json && json.status) {
                    setSessionData(json);
                } else {
                    setLoadError("Unable to load intervention data for this session.");
                }
            } catch (err) {
                setLoadError(err.message || "Unable to load intervention data.");
            } finally {
                setLoadingInterventions(false);
            }
        };

        fetchInterventions();
    }, [sessionId]);

    // ---- 1b. GET the goal rows being revised (Review Smart Goal only) ------
    // Best-effort: if it fails, the page still works, it just starts from a
    // blank goal instead of the pre-filled one.
    useEffect(() => {
        if (!needsSeparateGoalFetch) return;
        let cancelled = false;
        const fetchGoalRows = async () => {
            try {
                const token = localStorage.getItem("token");
                const res = await fetch(GET_INTERVENTIONS_API, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ session_id: parseInt(goalDataSessionId) }),
                });
                const json = await res.json();
                if (!cancelled && json && json.status) setGoalRowsSessionData(json);
            } catch (err) {
                if (!cancelled) console.error("Failed to load goals to revise:", err);
            }
        };
        fetchGoalRows();
        return () => { cancelled = true; };
    }, [needsSeparateGoalFetch, goalDataSessionId]);

    // The goal rows to pre-fill come from the goal session; everything else
    // (the interventions catalogue) comes from the review session.
    //
    // withNewestGoalVersion matters for Review Smart Goal: a revision that was
    // already saved exists only as a Goal Review snapshot on the goal session,
    // never as a planning row. Reading `goals` alone would reopen the form on
    // the goal as it was FIRST set, so the therapist would revise a stale copy
    // and the new row would carry the wrong values.
    const effectiveSessionData = useMemo(() => {
        const base = goalRowsSessionData || sessionData;
        if (!base) return base;
        return { ...sessionData, goals: withNewestGoalVersion(base).goals };
    }, [sessionData, goalRowsSessionData]);

    // ---- 2. Goal Domain options, derived from "goal_domain-goal_skill" groups ----
    // The API can return the same domain under different group keys with only
    // punctuation/spacing differences (e.g. "Play & Cognition" vs "Play and
    // Cognition"). Deduplicate by comparing a normalized form of the label so
    // each logically identical domain appears only once in the dropdown.
    const domainOptions = useMemo(() => {
        if (!sessionData?.data) return [];
        const normalizeName = (name) =>
            String(name || "")
                .toLowerCase()
                .replace(/&/g, "and")
                .replace(/[^a-z0-9]/g, "");
        const seenLabels = new Set();
        const options = [];
        Object.entries(sessionData.data).forEach(([groupKey, items]) => {
            const first = items?.[0] || {};
            const domain = first.goal_domain || groupKey;
            const skill = first.goal_skill || groupKey;
            const normDomain = normalizeName(domain);
            const normSkill = normalizeName(skill);
            const sameName = normSkill && normSkill === normDomain;
            const label = sameName ? domain : `${domain} - ${skill}`;
            const normalized = normalizeName(label);
            if (!normalized || seenLabels.has(normalized)) return;
            seenLabels.add(normalized);
            options.push({
                key: groupKey,
                goalDomain: domain,
                goalSkill: skill,
                label,
            });
        });
        return options;
    }, [sessionData]);

    // ---- 2b. Pre-select every available goal domain by default, so the
    // Goal Domain multi-select shows all domains as selected chips on load.
    useEffect(() => {
        if (!domainOptions.length) return;
        setGoals((prev) =>
            prev.map((goal) =>
                goal.goalDomainKeys.length
                    ? goal
                    : {
                          ...goal,
                          goalDomainKeys: domainOptions.map((d) => d.key),
                          goalDomains: domainOptions.map((d) => d.goalDomain),
                          goalSkills: domainOptions.map((d) => d.goalSkill),
                      }
            )
        );
    }, [domainOptions]);

    // ---- 2c. Previously-used Intervention Families (informational only) ----
    // Fetched separately from get_test_intervention.php because that endpoint is
    // scoped to the current session, whereas a duplicate check has to span every
    // session this client has ever had.
    //
    // The current session is excluded: re-opening a goal sheet that was already
    // saved on that session is an edit, not a historical duplicate.
    useEffect(() => {
        const clientId = sessionData?.session?.client_id;
        if (!clientId) return;

        let cancelled = false;

        const fetchUsed = async () => {
            try {
                const token = localStorage.getItem("token");
                const res = await fetch(GET_USED_INTERVENTIONS_API, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        client_id: clientId,
                        exclude_session_id: parseInt(sessionId),
                    }),
                });
                const json = await res.json();
                if (cancelled || !json?.status) return;

                // Keyed lowercase so the badge lookup matches regardless of the
                // casing the sheet was originally saved with.
                const lookup = {};
                (json.used_sheets || []).forEach((entry) => {
                    lookup[String(entry.excel_sheet || "").trim().toLowerCase()] = entry;
                });
                setUsedSheets(lookup);
            } catch (err) {
                // A failed badge lookup must never block goal entry - the
                // therapist simply sees no badges.
                if (!cancelled) setUsedSheets({});
            }
        };

        fetchUsed();
        return () => {
            cancelled = true;
        };
    }, [sessionData, sessionId]);

    // ---- 2d. Prefill from goals already saved on this session ----
    // get_test_intervention.php returns `goals` (pathway_goals + their
    // interventions) for the current session. When they exist, the therapist is
    // editing an existing plan rather than writing a new one, so the form has to
    // open populated instead of silently blank.
    //
    // Runs after the domain pre-select effect above, hence it deliberately
    // restores the saved domains rather than the "all domains" default.
    useEffect(() => {
        if (!effectiveSessionData) return;
        // Wait for this session's intervention catalogue too. domainOptions is
        // derived from it, and mapping the saved domains against an empty
        // catalogue would resolve every goal to no domains at all - then latch
        // prefilledForSessionRef and block the correct prefill.
        if (!sessionData) return;
        const savedGoals = effectiveSessionData.goals;
        if (!Array.isArray(savedGoals) || savedGoals.length === 0) return;
        if (prefilledForSessionRef.current === sessionId) return;
        prefilledForSessionRef.current = sessionId;

        // Map a saved goal back onto its dropdown option so the restored chips
        // carry the right `key`.
        //
        // The API returns goal_domain and goal_skill as two index-aligned
        // arrays, so the reliable key is the (domain, skill) PAIR. Matching on
        // the display label alone was ambiguous - "Motor" would also match any
        // option whose domain is Motor, and two options can share a domain -
        // so a per-index pair lookup is used first, with the flattened label as
        // a fallback for rows saved before the pair was returned separately.
        const optionKeyByLabel = {};
        const optionKeyByPair = {};
        domainOptions.forEach((opt) => {
            optionKeyByLabel[normalizeLabel(opt.label)] = opt.key;
            optionKeyByPair[normalizeLabel(opt.goalDomain) + "||" + normalizeLabel(opt.goalSkill)] = opt.key;
        });

        const restored = savedGoals.map((saved) => {
            const savedDomains = Array.isArray(saved.goal_domain) ? saved.goal_domain : [];
            const savedSkills = Array.isArray(saved.goal_skill) ? saved.goal_skill : [];
            const keys = savedDomains
                .map((domain, i) => {
                    const skill = String(savedSkills[i] ?? "");
                    if (skill) {
                        const byPair = optionKeyByPair[
                            normalizeLabel(domain) + "||" + normalizeLabel(skill)
                        ];
                        if (byPair) return byPair;
                    }
                    // No skill recorded for this slot: the domain may itself
                    // carry a glued "Domain-Skill" value from an older save.
                    return optionKeyByLabel[normalizeLabel(domain)];
                })
                .filter(Boolean);

            // Drop any saved label with no matching option: the intervention
            // catalogue may have been re-uploaded since, and offering a chip
            // that cannot be re-submitted would corrupt the next save.
            const domains = keys
                .map((key) => domainOptions.find((d) => d.key === key)?.goalDomain)
                .filter(Boolean);
            const skills = keys
                .map((key) => domainOptions.find((d) => d.key === key)?.goalSkill)
                .filter(Boolean);

            const savedInterventions = Array.isArray(saved.interventions) ? saved.interventions : [];
            const first = savedInterventions[0] || {};
            const selectedSheets = Array.isArray(first.selected_intervention)
                ? first.selected_intervention
                : [];

            return {
                ...createNewGoal(),
                id: saved.goal_id || Date.now() + Math.random(),
                // A goal saved with no domain at all is still worth restoring;
                // fall back to the "select all" default in that case.
                goalDomainKeys: keys.length ? keys : domainOptions.map((d) => d.key),
                goalDomains: keys.length ? domains : domainOptions.map((d) => d.goalDomain),
                goalSkills: keys.length ? skills : domainOptions.map((d) => d.goalSkill),
                status: saved.status || "Select",
                resultDate: saved.result_date || "",
                // Restoring the target date is not cosmetic: buildPayload() posts
                // goal.targetDate, so leaving it unset would clear the stored date
                // every time the therapist revises a goal without touching it.
                targetDate: saved.target_date || "",
                smartGoal: saved.smart_goal || "",
                goalReviewDate: saved.goal_review_date || "",
                outcomeMeasure: saved.outcome_measure || "",
                interventionTypes: selectedSheets,
                interventions: [
                    {
                        type: selectedSheets[0] || "",
                        // These arrive under their column names, not the form's:
                        // dosage_minutes_per_day / dosage_weeks.
                        minutesPerDay: toCount(first.dosage_minutes_per_day),
                        weeks: toCount(first.dosage_weeks),
                        therapistSessions: toCount(first.therapist_sessions),
                    },
                ],
            };
        });

        if (restored.length > 0) setGoals(restored);
        // Must key off effectiveSessionData, not sessionData. In Review Smart Goal
        // mode the goals arrive from a SECOND request (the goal session), and that
        // response can land after this session's catalogue. Depending on
        // sessionData alone meant the effect ran once with an empty goals array,
        // returned early, and never re-ran - so the form opened completely blank.
        //
        // domainOptions is intentionally omitted: it is derived from the same
        // sessionData payload and is already settled at this point, and
        // re-running on it would clobber the therapist's later edits. The
        // prefilledForSessionRef guard keeps a later re-run from overwriting edits.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveSessionData, sessionId]);

    // ---- 3. Intervention options for the currently selected domain group ----
    // The API returns one row per intervention *sub-section* (e.g. "Switching",
    // "Key Components of Switching", "Tailoring Switching for the Identified
    // Goal" ...), all sharing the same excel_sheet value ("SWITCHING"). The
    // dropdown - and everything derived from it (goal.interventions, the
    // numbered dosage blocks, and intervention_type in the API payload) -
    // should only show/use the deduplicated excel_sheet name, not every
    // sub-title row.
    const getInterventionTitleOptions = (goalDomainKeys) => {
        if (!sessionData?.data || !goalDomainKeys?.length) return [];
        const titles = new Set();
        goalDomainKeys.forEach(key => {
            const items = sessionData.data[key] || [];
            items.forEach(i => { if (i.excel_sheet) titles.add(i.excel_sheet); });
        });
        return [...titles];
    };

    const handleInterventionChange = (index, selectedValues) => {
        setGoals((prev) =>
            prev.map((goal, i) => {
                if (i !== index) return goal;
                return { ...goal, interventionTypes: selectedValues };
            })
        );
    };

    // Update a specific field for a specific goal index
    const updateGoalField = (index, field, value) => {
        const updatedGoals = [...goals];
        updatedGoals[index][field] = value;
        setGoals(updatedGoals);
    };

    // Handle goal domain multi-select
    const handleDomainChange = (index, selected) => {
        const selectedArr = Array.isArray(selected) ? selected : [];
        const keys = selectedArr.map(o => o.value || o);
        const domains = keys.map(k => {
            const opt = domainOptions.find(d => d.key === k);
            return opt ? opt.goalDomain : k;
        });
        const skills = keys.map(k => {
            const opt = domainOptions.find(d => d.key === k);
            return opt ? opt.goalSkill : k;
        });
        const updatedGoals = [...goals];
        updatedGoals[index] = {
            ...updatedGoals[index],
            goalDomainKeys: keys,
            goalDomains: domains,
            goalSkills: skills,
            interventionTypes: [],
        };
        setGoals(updatedGoals);
    };


    // ---- 4. POST the goals to insert_pathway_goals.php ----
    const buildPayload = () => ({
        session_id: Number(sessionId),
        // When revising a smart goal, pass the originating session so the
        // backend can update the existing goal row instead of creating a new one.
        ...(isSmartGoal && goalSessionId ? { goal_session_id: Number(goalSessionId) } : {}),
        goals: goals.map((goal) => ({
            goal_domain: goal.goalDomains.length ? goal.goalDomains : [""],
            goal_skill: goal.goalSkills.length ? goal.goalSkills : [""],
            status: goal.status,
            result_date: goal.resultDate,
            target_date: goal.targetDate,
            smart_goal: goal.smartGoal,
            goal_review_date: goal.goalReviewDate,
            outcome_measure: goal.outcomeMeasure,
            interventions: [{
                selected_intervention: goal.interventionTypes,
                dosage_minutes_per_day: goal.interventions[0]?.minutesPerDay === "" || goal.interventions[0]?.minutesPerDay == null ? 0 : Number(goal.interventions[0].minutesPerDay),
                dosage_weeks: goal.interventions[0]?.weeks === "" || goal.interventions[0]?.weeks == null ? 0 : Number(goal.interventions[0].weeks),
                therapist_sessions: goal.interventions[0]?.therapistSessions === "" || goal.interventions[0]?.therapistSessions == null ? 0 : Number(goal.interventions[0].therapistSessions),
            }],
        })),
    });

    const handleUpdate = async () => {
        try {
            setSubmitting(true);
            setSubmitError(null);
            const token = localStorage.getItem("token");
            const res = await fetch(POST_GOALS_API, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(buildPayload()),
            });
            const json = await res.json();
            if (json && json.status) {
                // Target-date reminders are generated from the saved goal rows
                // on the server, so there is no reminder row to create here.
                // The popup re-checks now so a target date set to today shows
                // up immediately; future dates raise their reminder on the day.
                window.dispatchEvent(new Event("reminder-created"));

                // Return to the download page for the session that HOLDS the goal.
                // In Review Smart Goal mode that is goalDataSessionId - the
                // revision was written there, so the originating session is what
                // has to be re-read. Using the review session would show the
                // unchanged state.
                navigate(`/goalsetdownload?sessionId=${goalDataSessionId}`);
                setToast({ message: "Goals saved successfully.", type: "success" });
            } else {
                setSubmitError(json?.message || "Failed to save goals.");
                setToast({ message: json?.message || "Failed to save goals.", type: "error" });
            }
        } catch (err) {
            setSubmitError(err.message || "Failed to save goals.");
            setToast({ message: err.message || "Failed to save goals.", type: "error" });
        } finally {
            setSubmitting(false);
        }
    };

    // ---- 6. Client name for the "Goals selected for" heading ----
    const clientId = sessionData?.session?.client_name;

    // ---- 7. Navigation between the 5 foundational intervention cards inside the modal ----
    const handlePrevCard = () => {
        setActiveCardIndex((prev) =>
            prev === null
                ? prev
                : (prev - 1 + FOUNDATIONAL_CARDS.length) % FOUNDATIONAL_CARDS.length
        );
    };

    const handleNextCard = () => {
        setActiveCardIndex((prev) =>
            prev === null ? prev : (prev + 1) % FOUNDATIONAL_CARDS.length
        );
    };

    /*
     * `goals` is seeded with `[createNewGoal()]`, so before the fetch lands this
     * page rendered a complete but EMPTY SMART goal form - blank smart goal, blank
     * domain, blank date, blank dosage - directly underneath "Loading
     * interventions?". Two states on screen at once, and the blank form looks like
     * real data you could edit and save over. It also showed the previous session's
     * goals after any re-fetch.
     *
     * Early-return while loading so only one state is ever painted. The Update
     * button lives inside the loaded content, so nothing reachable gets unmounted.
     */
    if (loadingInterventions) {
        return (
            <div className="update-goal-page">
                <div className="ug-header">
                    <div className="ug-title-container">
                        <h1 className="common-page-title"><span className="sr-only">Update Goal: </span>Intervention Planning</h1>
                        <p className="common-page-subtitle">Update your intervention planning here</p>
                    </div>
                </div>
                <p className="ug-loading-text" role="status" aria-live="polite">
                    Loading interventions...
                </p>
            </div>
        );
    }

    return (
        <div className="update-goal-page">
            {/* Header */}
            <div className="ug-header">
                <div
                    className="back-btn2"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(-1)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            navigate(-1);
                        }
                    }}
                    style={{ cursor: "pointer" }}
                >
                    <FaArrowLeft className="back-icon" aria-hidden="true" />
                    Previous page
                </div>
                <div className="ug-title-container">
                    <h1 className="common-page-title"><span className="sr-only">Update Goal: </span>Intervention Planning</h1>
                    <p className="common-page-subtitle">Update your intervention planning here</p>
                </div>
                {/* <div className="ug-icon">
                    <div className="pd-icon-box blue"><FontAwesomeIcon icon={faFileLines} /></div>
                    <span>Goals Review</span>
                </div> */}
            </div>

            {loadingInterventions && (
                <p className="ug-loading-text">Loading interventions?</p>
            )}
            {loadError && <p className="ug-error-text">{loadError}</p>}

            {/* Map through the goals array to generate dynamic blocks */}
            {goals.map((goal, index) => {
                const domainSelectOptions = domainOptions.map(option => ({ value: option.key, label: option.label }));
                const interventionOptions = goal.goalDomainKeys.length
                    ? getInterventionTitleOptions(goal.goalDomainKeys).map((title) => ({
                        value: title,
                        label: title,
                    }))
                    : [];

                return (
                    <div key={goal.id} className="ug-goal-block-wrapper">
                        {/* Section Title - Stays outside the map */}
                        <div className="ug-section-title">
                            <h3>{clientId ? clientId : "-"}'s Goals</h3>
                        </div>


                        {/* Goal Domain + SMART goal on one row (Goal Domain 1fr, SMART 3fr) */}
                        <div className="ug-goal-smart-row">
                            <div className="ug-field">
                                <label htmlFor={`ug-goal-domain-${index}`}>Goal Domain</label>
                                <Select
                                    isMulti
                                    inputId={`ug-goal-domain-${index}`}
                                    options={domainSelectOptions}
                                    placeholder="Select"
                                    value={domainSelectOptions.filter(opt => goal.goalDomainKeys.includes(opt.value))}
                                    onChange={(selected) => handleDomainChange(index, selected || [])}
                                    closeMenuOnSelect={false}
                                    styles={goalDomainSelectStyles}
                                    /* Read-only: the domains are derived from the
                                       GET response, not chosen by the user. Keep the
                                       chips visible but strip every way to edit:
                                       no clear "x", no per-chip remove, no search box,
                                       no opening the menu, and out of the tab order. */
                                    isClearable={false}
                                    isSearchable={false}
                                    openMenuOnClick={false}
                                    tabIndex={-1}
                                    isOptionDisabled={() => true}
                                    components={{ MultiValueRemove: () => null }}
                                />
                            </div>

                        </div>
                        <div className="ug-field full-width">
                            <div className="ug-field">
                                <label htmlFor={`ug-smart-goal-${index}`}>Write in the infant's SMART goal related to the goal domains selected.</label>
                                <input type="text" id={`ug-smart-goal-${index}`} value={goal.smartGoal} onChange={(e) => updateGoalField(index, "smartGoal", e.target.value)} placeholder="Type here......" />
                            </div>
                        </div>
                        {/* Intervention Selection - checkbox list, populated from get_test_intervention.php */}
                        <div className="ug-field full-width">
                            <label>
                                Select the intervention/s choices that have been made in collaboration
                                with the family
                            </label>

                            <div className="ug-multiselect-field">
                                {interventionOptions.length === 0 ? (
                                    <div style={{ border: "1px solid #d5dbe0", borderRadius: 8, padding: "10px 14px", fontSize: 14, color: "var(--cpa-gray-2, #626A6F)" }}>
                                        {goal.goalDomainKeys.length ? "No interventions available." : "Select a Goal Domain first."}
                                    </div>
                                ) : (
                                    <div style={{ border: "1px solid #d5dbe0", borderRadius: 8, overflow: "hidden" }}>
                                        {interventionOptions.map((option) => {
                                            const isChecked = goal.interventionTypes.includes(option.value);
                                            // Informational duplicate check. The spec asks
                                            // for a "Previously used" marker, NOT a block -
                                            // re-prescribing the same family across a
                                            // Review is legitimate continuity of care, so
                                            // the checkbox stays fully enabled.
                                            const priorUse = usedSheets[String(option.value || "").trim().toLowerCase()];
                                            return (
                                                <label
                                                    className="ug-multiselect-option"
                                                    key={option.value}
                                                    style={{ color: "#323C43", fontWeight: 700, background: isChecked ? "#EBF7E9" : "#fff" }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isChecked}
                                                        onChange={(e) => {
                                                            const current = goal.interventionTypes || [];
                                                            const next = e.target.checked
                                                                ? [...current, option.value]
                                                                : current.filter((v) => v !== option.value);
                                                            handleInterventionChange(index, next);
                                                        }}
                                                    />
                                                    <span style={{ color: "#323C43", fontWeight: 700 }}>{option.label}</span>
                                                    {priorUse && (
                                                        <span
                                                            className="ug-previously-used-badge"
                                                            title={describePriorUse(priorUse)}
                                                        >
                                                            Previously used
                                                        </span>
                                                    )}
                                                </label>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Dosage Home Practice */}
                        <div className="grv-sub-title">Dosage of Home Practice to Support this Goal</div>
                        <div className="grv-counters-3col">
                            <div className="grv-counter-field">
                                <label>Number of minutes per day</label>
                                <div className="grv-counter">
                                    <button type="button" onClick={() => setGoals(prev => { const g = [...prev]; const iv = g[index].interventions[0]; g[index] = { ...g[index], interventions: [{ ...iv, minutesPerDay: Math.max(0, (iv.minutesPerDay || 0) - 1) }] }; return g; })} aria-label="Decrease minutes per day">-</button>
                                    <input type="number" min="0" className="grv-counter-input"
                                        value={goal.interventions[0]?.minutesPerDay ?? ""}
                                        onChange={(e) => { const v = e.target.value; setGoals(prev => { const g = [...prev]; g[index] = { ...g[index], interventions: [{ ...g[index].interventions[0], minutesPerDay: v }] }; return g; }); }}
                                        onFocus={() => setGoals(prev => { const g = [...prev]; const iv = g[index].interventions[0]; if (iv.minutesPerDay === 0 || iv.minutesPerDay === "0") { g[index] = { ...g[index], interventions: [{ ...iv, minutesPerDay: "" }] }; } return g; })}
                                        onBlur={() => setGoals(prev => { const g = [...prev]; const iv = g[index].interventions[0]; if (iv.minutesPerDay === "" || iv.minutesPerDay == null) { g[index] = { ...g[index], interventions: [{ ...iv, minutesPerDay: 0 }] }; } return g; })}
                                        onWheel={(e) => e.target.blur()}
                                        aria-label="Minutes per day" />
                                    <button type="button" onClick={() => setGoals(prev => { const g = [...prev]; const iv = g[index].interventions[0]; g[index] = { ...g[index], interventions: [{ ...iv, minutesPerDay: Math.max(0, (iv.minutesPerDay || 0) + 1) }] }; return g; })} aria-label="Increase minutes per day">+</button>
                                </div>
                            </div>
                            <div className="grv-counter-field">
                                <label>Number of weeks</label>
                                <div className="grv-counter">
                                    <button type="button" onClick={() => setGoals(prev => { const g = [...prev]; const iv = g[index].interventions[0]; g[index] = { ...g[index], interventions: [{ ...iv, weeks: Math.max(0, (iv.weeks || 0) - 1) }] }; return g; })} aria-label="Decrease weeks">-</button>
                                    <input type="number" min="0" className="grv-counter-input"
                                        value={goal.interventions[0]?.weeks ?? ""}
                                        onChange={(e) => { const v = e.target.value; setGoals(prev => { const g = [...prev]; g[index] = { ...g[index], interventions: [{ ...g[index].interventions[0], weeks: v }] }; return g; }); }}
                                        onFocus={() => setGoals(prev => { const g = [...prev]; const iv = g[index].interventions[0]; if (iv.weeks === 0 || iv.weeks === "0") { g[index] = { ...g[index], interventions: [{ ...iv, weeks: "" }] }; } return g; })}
                                        onBlur={() => setGoals(prev => { const g = [...prev]; const iv = g[index].interventions[0]; if (iv.weeks === "" || iv.weeks == null) { g[index] = { ...g[index], interventions: [{ ...iv, weeks: 0 }] }; } return g; })}
                                        onWheel={(e) => e.target.blur()}
                                        aria-label="Number of weeks" />
                                    <button type="button" onClick={() => setGoals(prev => { const g = [...prev]; const iv = g[index].interventions[0]; g[index] = { ...g[index], interventions: [{ ...iv, weeks: Math.max(0, (iv.weeks || 0) + 1) }] }; return g; })} aria-label="Increase weeks">+</button>
                                </div>
                            </div>
                        </div>

                        {/* Therapist Sessions to Support */}
                        <div className="grv-sub-title">Therapists Sessions to Support this Goal</div>
                        <div className="grv-counters-1col">
                            <div className="grv-counter-field">
                                <label>Number of Sessions</label>
                                <div className="grv-counter">
                                    <button type="button" onClick={() => setGoals(prev => { const g = [...prev]; const iv = g[index].interventions[0]; g[index] = { ...g[index], interventions: [{ ...iv, therapistSessions: Math.max(0, (iv.therapistSessions || 0) - 1) }] }; return g; })} aria-label="Decrease therapist sessions">-</button>
                                    <input type="number" min="0" className="grv-counter-input"
                                        value={goal.interventions[0]?.therapistSessions ?? ""}
                                        onChange={(e) => { const v = e.target.value; setGoals(prev => { const g = [...prev]; g[index] = { ...g[index], interventions: [{ ...g[index].interventions[0], therapistSessions: v }] }; return g; }); }}
                                        onFocus={() => setGoals(prev => { const g = [...prev]; const iv = g[index].interventions[0]; if (iv.therapistSessions === 0 || iv.therapistSessions === "0") { g[index] = { ...g[index], interventions: [{ ...iv, therapistSessions: "" }] }; } return g; })}
                                        onBlur={() => setGoals(prev => { const g = [...prev]; const iv = g[index].interventions[0]; if (iv.therapistSessions === "" || iv.therapistSessions == null) { g[index] = { ...g[index], interventions: [{ ...iv, therapistSessions: 0 }] }; } return g; })}
                                        onWheel={(e) => e.target.blur()}
                                        aria-label="Number of therapist sessions" />
                                    <button type="button" onClick={() => setGoals(prev => { const g = [...prev]; const iv = g[index].interventions[0]; g[index] = { ...g[index], interventions: [{ ...iv, therapistSessions: Math.max(0, (iv.therapistSessions || 0) + 1) }] }; return g; })} aria-label="Increase therapist sessions">+</button>
                                </div>
                            </div>
                        </div>

                        {/* Outcome Measure */}
                        <div className="ug-field full-width">
                            <label htmlFor={`ug-outcome-${index}`}>What outcome measure are you using?</label>
                            <textarea id={`ug-outcome-${index}`} value={goal.outcomeMeasure}
                                onChange={(e) => updateGoalField(index, "outcomeMeasure", e.target.value)}
                                placeholder="Type here" rows={3} />
                        </div>

                        {/* Target Date */}
                        <div className="ug-form-grid">
                            <div className="ug-field">
                                <label htmlFor={`ug-result-date-${index}`}>Goal Target Date</label>
                                <div className="ug-date-input-wrapper">
                                    <input type="date" id={`ug-result-date-${index}`}
                                        value={goal.targetDate} min={today}
                                        onChange={(e) => updateGoalField(index, "targetDate", e.target.value)} />
                                    <FaCalendarAlt className="ug-date-icon" aria-hidden="true"
                                        onClick={() => document.getElementById(`ug-result-date-${index}`)?.showPicker()} />
                                </div>
                            </div>
                        </div>


                    </div>
                );
            })}

            {submitError && <p className="ug-error-text">{submitError}</p>}
            <div className="ug-goal-block-wrapper">
                {/* Info Box: mCIMT vs Bimanual */}
                <div className="ug-info-box">
                    <h4 className="ug-info-title">Foundational Interventions</h4>
                    <p className="ug-info-text">
                        Foundational interventions should be embedded in conjunction with specific interventions when working with infants with cerebral palsy and their caregivers: home programs, play, coaching, supporting caregivers and environmental enrichment.
                    </p>
                </div>

                {/* 5 Cards Grid - click a card to preview it in the popup */}
                <div className="ug-cards-grid">
                    {FOUNDATIONAL_CARDS.map((card, cardIdx) => (
                        <div
                            className="ug-card"
                            key={card.title}
                            onClick={() => setActiveCardIndex(cardIdx)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    setActiveCardIndex(cardIdx);
                                }
                            }}
                            role="button"
                            tabIndex={0}
                            style={{ cursor: "pointer" }}
                        >
                            <div className="ug-card-image">
                                <img src={card.image} alt="" aria-hidden="true" />
                            </div>
                            <div className="ug-card-content">
                                <h5>{card.title}</h5>

                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Footer Buttons */}
            <div className="ug-footer" style={{ justifyContent: "center" }}>
                <button className="ug-update-btn" onClick={handleUpdate} disabled={submitting}>
                    {submitting ? "Saving..." : "Complete Intervention Planning"}
                </button>
            </div>

            {/* Foundational intervention card popup - matches the provided reference design.
               Not remounted via key anymore (the modal now stays mounted while cycling
               through cards via the arrows), the description just resets when the
               underlying card index changes. */}
            {activeCardIndex !== null && (
                <FoundationalCardModal
                    card={FOUNDATIONAL_CARDS[activeCardIndex]}
                    cardIndex={activeCardIndex}
                    total={FOUNDATIONAL_CARDS.length}
                    onClose={() => setActiveCardIndex(null)}
                    onPrev={handlePrevCard}
                    onNext={handleNextCard}
                />
            )}

            {/* Styles for the new multi-select dropdown and the card popup modal.
               Added here (rather than the existing CSS files) so nothing already
               in review.css / UpdateGoal.css is touched. */}
            <style>{`
                .ug-multiselect-field { position: relative; }
                .ug-multiselect { position: relative; }
                .ug-multiselect-control {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border: 1px solid #d5dbe0;
                    border-radius: 8px;
                    padding: 10px 14px;
                    background: #fff;
                    cursor: pointer;
                    font-size: 14px;
                    color: #333;
                }
                .ug-multiselect-control-text {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    max-width: 90%;
                }
                .ug-multiselect-arrow { color: var(--cpa-gray-2, #626A6F); margin-left: 8px; }
                .ug-multiselect-option {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 14px;
    font-size: 14px;
    line-height: 1.5;
    cursor: pointer;
    white-space: normal;
    word-break: break-word;
}

.ug-multiselect-option input[type="checkbox"] {
    margin: 3px 0 0;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
}

.ug-multiselect-option:hover {
    background: #ffffff;
}

.ug-multiselect-option span {
    flex: 1;
    text-align: left;
}

/* "Previously used" duplicate-check badge.
   Deliberately informational: it must read as a note, not as an error, because
   re-prescribing the same family is a legitimate clinical decision. The
   selector is more specific than the generic span rule above so the badge
   keeps its own pill sizing instead of inheriting flex: 1. */
.ug-multiselect-option .ug-previously-used-badge {
    flex: 0 0 auto;
    margin-left: auto;
    align-self: center;
    padding: 2px 8px;
    border-radius: 999px;
    background: #fff4e5;
    border: 1px solid #f0b429;
    color: #8a5a00;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.2px;
    white-space: nowrap;
    cursor: help;
    text-align: center;
}
                .ug-loading-text, .ug-error-text {
                    padding: 0 20px;
                    font-size: 14px;
                }
                .ug-error-text { color: var(--cpa-error-red, #C22B27); }

                .ug-counter-input {
                    width: 46px;
                    text-align: center;
                   
                    border-radius: 4px;
                    padding: 4px 2px;
                    font-size: 13px;
                    color: #333;
                }
                .ug-counter-input:focus {
                    outline: none;
                    border-color: #4caf50;
                    box-shadow: 0 0 0 1px #4caf50;
                }
                /* hide native number spinner so only our +/- buttons show */
                .ug-counter-input::-webkit-outer-spin-button,
                .ug-counter-input::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                .ug-counter-input[type="number"] {
                    -moz-appearance: textfield;
                }

                .grv-sub-title {
                    font-weight: 700;
                    color: #247A33;
                    margin: 14px 0 10px;
                    font-size: 14px;
                }
                .grv-counters-3col {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 4px;
                    margin-bottom: 4px;
                }
                .grv-counters-1col {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 4px;
                }
                .grv-counter-field {
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                    max-width: 400px;
                    margin-bottom: 20px;
                }
                .grv-counter-field label {
                    font-size: 13px;
                    color: #247A33;
                    font-weight: 700;
                    display: inline-block;
                }
                .grv-counter {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    flex-shrink: 0;
                }
                .grv-counter-input {
                    border: 1px solid var(--cpa-gray-3, #737A7E);
                    border-radius: 5px;
                    background: #fff;
                    font-weight: 600;
                    color: #333;
                    width: 52px;
                    height: 39px;
                    box-sizing: border-box;
                    text-align: center;
                    font-size: 14px;
                    padding: 0;
                    font-family: inherit;
                    -moz-appearance: textfield;
                }
                .grv-counter-input::-webkit-outer-spin-button,
                .grv-counter-input::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                .grv-counter button {
                    background: #0063A5;
                    color: #fff;
                    border: none;
                    border-radius: 6px;
                    width: 28px;
                    height: 39px;
                    box-sizing: border-box;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    font-size: 18px;
                    font-weight: bold;
                    padding: 0;
                }
                .grv-counter button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                .grv-counter button:first-child { border-top-right-radius: 0; border-bottom-right-radius: 0; }
                .grv-counter button:last-child { border-top-left-radius: 0; border-bottom-left-radius: 0; }

                @keyframes ug-select-expand {
                    from { opacity: 0; transform: scaleY(0.85); }
                    to { opacity: 1; transform: scaleY(1); }
                }

                .ug-card-modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.45);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 20px;
                }
                .ug-card-modal {
                    width: 100%;
                    max-width: 1020px;
                    max-height:500px;
                    background: #ffffff;
                    border-radius: 16px;
                    overflow: hidden;
                    box-shadow: 0 12px 40px rgba(0,0,0,0.25);
                }
                .ug-card-modal-header {
                    background: #2f7a3d;
                    color: #fff;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 16px 20px;
                }
                .ug-card-modal-header h3 {
                    margin: 0;
                    font-size: 20px;
                    font-weight: 700;
                }
                .ug-card-modal-close {
                    background: #2f7a3d;
                    color: var(--cpa-white, #fff);
                    border: none;
                    width: 28px;
                    height: 28px;
                    border-radius: 50%;
                    font-size: 16px;
                    line-height: 1;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .ug-card-modal-body {
                    display: flex;
                    gap: 24px;
                    padding: 35px;
                    align-items: flex-start;
                    max-height: 400px;
                    overflow-y: auto;
                }
                .ug-card-modal-text {
                    flex: 1.2;
                    font-size: 14px;
                    line-height: 2.1;
                    color: #24352a;
                    text-align:justify;
                }
                .ug-card-modal-image {
                    flex: 1;
                    border: 2px solid #2f7a3d;
                    border-radius: 12px;
                    overflow: hidden;
                    height:200px;
                    width:100px;
                }
                .ug-card-modal-image img {
                    width: 100%;
                    height: auto;
                    display: block;
                }
                .ug-card-modal-footer {
                    display: flex;
                    align-items: center;
                    justify-content: right;
                    background: #d9efd9;
                    padding: 14px 24px;
                }
                .ug-card-modal-learnmore {
                    color: #2f7a3d;
                    font-weight: 600;
                    font-size: 14px;
                    cursor: pointer;
                }
                .ug-card-modal-nav {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                }
                .ug-card-modal-nav button {
                    background: #2f7a3d;
                    border: none;
                    width: 28px;
                    height: 28px;
                    border-radius: 50%;
                    cursor: pointer;
                    color: #fff;
                    font-size: 14px;
                    transition: background 0.15s ease;
                }
                .ug-card-modal-nav button:hover {
                    background: #2f7a3d;
                }
                .ug-card-modal-nav span {
                    font-size: 13px;
                    color: #2f7a3d;
                    font-weight: 600;
                }
                .read-more {
                    color: #2f7a3d;
                    font-weight: 600;
                    cursor: pointer;
                    display: inline-block;
                    margin-top: 6px;
                }
                .description-text {
                    margin: 0;
                }
                .key-components-title {
                    font-size: 22px;
                    font-weight: 700;
                    margin-bottom: 12px;
                    margin-top: 16px;
                }

                .key-components-list {
                    padding-left: 28px;
                    margin: 0;
                }

                .key-components-list li {
                    text-align: justify;
                    line-height: 1.8;
                    margin-bottom: 12px;
                }

                .key-components-list li strong {
                    font-weight: 700;
                }

                .footer-text {
                    margin-top: 20px;
                    text-align: justify;
                }
                @media (max-width: 600px) {
                    .ug-card-modal-body { flex-direction: column; }
                }
            `}</style>
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
};

// Popup shown when a Foundational Interventions card is clicked.
// Layout/colours follow the reference design: green header with a
// close button, image on the right, description on the left, and a footer
// with page navigation that now cycles through all 5 cards via
// onPrev/onNext (passed down from the parent, which owns activeCardIndex).
const FoundationalCardModal = ({ card, cardIndex, total, onClose, onPrev, onNext }) => {
    // Reset "read more" state whenever the visible card changes, so each
    // card opens back at the collapsed/summary view.
    const [showFullDescription, setShowFullDescription] = useState(false);

    useEffect(() => {
        setShowFullDescription(false);
    }, [cardIndex]);

    if (!card) return null;

    return (
        <div className="ug-card-modal-overlay" onClick={onClose} onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); onClose(); } }} role="dialog" aria-modal="true" aria-label={card.title} tabIndex="-1">
            <div className="ug-card-modal" onClick={(e) => e.stopPropagation()}>
                <div className="ug-card-modal-header">
                    <h3 style={{color:"#ffffff"}}>{card.title}</h3>
                    <button className="ug-card-modal-close" onClick={onClose} aria-label="Close">
                        <b>&#10005;</b>
                    </button>
                </div>
                <div className="ug-card-modal-body">
                    <div className="ug-card-modal-text">
                        <p className={showFullDescription ? "description-text expanded" : "description-text"}>{card.description}</p>
                        {!showFullDescription && (
                            <span
                                className="read-more"
                                role="button"
                                tabIndex="0"
                                onClick={() => setShowFullDescription(true)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowFullDescription(true); } }}
                                aria-expanded="false"
                            >
                                Read More...
                            </span>
                        )}
                        {showFullDescription && (
                            <>
                                <div className="key-components-title">
                                    Key components
                                </div>

                                <ul className="key-components-list">
                                    {card.keyComponents.map((item, i) => (
                                        <li key={i}>
                                            <strong>{item.title}:</strong> {item.text}
                                        </li>
                                    ))}
                                </ul>

                                {card.footer && (
                                    <p className="footer-text">
                                        {card.footer}
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                    <div className="ug-card-modal-image">
                        <img src={card.image} alt="" aria-hidden="true" />
                    </div>
                </div>
                <div className="ug-card-modal-footer">

                    <div className="ug-card-modal-nav">
                        <button onClick={onPrev} aria-label="Previous">&#10094;</button>
                        <span>{cardIndex + 1}/{total}</span>
                        <button onClick={onNext} aria-label="Next">&#10095;</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UpdateGoal;
